import {useSharedFilter} from '../../../shared/navigation/useSharedReport';
// /src/components/SEEC/sections/UnifiedSummaryReportSection.jsx

/**
 * Summary report section showing contribution and expenditure data.
 * - Pulls Firestore summary documents for one or both parties
 * - Renders line graphs for report metrics over time
 * - Renders bar graphs showing donation statistics
 * - Provides toggleable views and summary tables
 */

import React, { useEffect, useMemo, useState } from "react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { CommonTable } from "../../../shared/ui/table/CommonTable";
import { LineChartWithToggles } from "../../../shared/ui/chart/LineChartWithToggles.jsx";
import { getChartColor } from "../../../shared/constants/chartColors";
import { HoverPopover } from "../../../shared/ui/overlay/HoverPopover.jsx";
import seecPdfManifest from "../../../shared/constants/seecPdfManifest.json";
import { toJSDate, formatMMDDYYYY } from "../utils/componentsUtils";
import {
  loadSeecDocument,
  loadSeecDocuments,
} from "../utils/loadSeecDocuments";

function sanitizeCommitteeLabel(value) {
  return String(value || "")
    .replace(/\s*\(SEEC\d+\)\s*$/i, "")
    .trim();
}

function normalizeDateKeyPart(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const parsed = toJSDate(raw);
  if (!(parsed instanceof Date) || Number.isNaN(parsed.getTime())) {
    return raw;
  }

  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  const year = parsed.getFullYear();
  return `${month}/${day}/${year}`;
}

function buildManifestLookupKey(
  committeeName,
  filingEventType,
  beginDate,
  endDate,
) {
  return [
    sanitizeCommitteeLabel(committeeName).toLowerCase(),
    String(filingEventType || "")
      .trim()
      .toLowerCase(),
    normalizeDateKeyPart(beginDate),
    normalizeDateKeyPart(endDate),
  ].join("|");
}

function buildSummaryPopoverRows(row) {
  const orderedFields = [
    ["committeeDisplayName", "Committee"],
    ["FilingEventType", "Filing Event Type"],
    [
      "beginningBalance",
      "Balance on hand at the beginning of Reporting Period",
    ],
    ["receipts", "Total Monetary Receipts"],
    ["expenses", "Expenses Paid by Committee"],
    ["endingBalance", "Balance on hand at close of Reporting Period"],
    ["beginningLoanBalance", "Beginning Loan Balance"],
    ["disclosureSubmittedDate", "Disclosure Submitted Date"],
    ["periodCovBeginDate", "Period Cov. Begin Date"],
    ["periodCovEndDate", "Period Cov. End Date"],
    ["committeeType", "Committee Type"],
    ["status", "Status"],
    ["dataSource", "Data Source"],
    ["partyAffiliation", "Party Affiliation"],
    ["refiledElectronically", "Refiled Electronically"],
  ];

  const currencyKeys = new Set([
    "beginningBalance",
    "receipts",
    "expenses",
    "endingBalance",
    "beginningLoanBalance",
  ]);

  return orderedFields
    .map(([key, label]) => {
      const value = row?.[key];
      if (value == null) return null;
      if (typeof value === "string" && value.trim() === "") return null;
      return {
        key,
        label,
        value: currencyKeys.has(key)
          ? `$${Number(value || 0).toLocaleString()}`
          : String(value),
      };
    })
    .filter(Boolean);
}

function UnifiedSummaryReportSection({
  collapsed,
  toggle,
  title = "Summary Report",
  docIds = ["GDTC-Summaries"],
  rows = null,
  contributionRows = null,
  comparisonRows = null,
  comparisonContributionRows = null,
  townName = "",
  currentCommitteeName = "",
}) {
  // State management
  const [reportData, setReportData] = useState([]);
  const [showTable, setShowTable] = useSharedFilter("UnifiedSummaryReportSection.jsx:UnifiedSummaryReportSection:showTable:" + [title].join(":"), true);
  const [showBeginningBalanceChart, setShowBeginningBalanceChart] =
    useSharedFilter("UnifiedSummaryReportSection.jsx:UnifiedSummaryReportSection:showBeginningBalanceChart:" + [title].join(":"), false);
  const [showEndingBalanceChart, setShowEndingBalanceChart] = useSharedFilter("UnifiedSummaryReportSection.jsx:UnifiedSummaryReportSection:showEndingBalanceChart:" + [title].join(":"), false);
  const [showReceiptsChart, setShowReceiptsChart] = useSharedFilter("UnifiedSummaryReportSection.jsx:UnifiedSummaryReportSection:showReceiptsChart:" + [title].join(":"), false);
  const [showExpensesChart, setShowExpensesChart] = useSharedFilter("UnifiedSummaryReportSection.jsx:UnifiedSummaryReportSection:showExpensesChart:" + [title].join(":"), false);
  const [beginningBalanceYearRange, setBeginningBalanceYearRange] =
    useSharedFilter("UnifiedSummaryReportSection.jsx:UnifiedSummaryReportSection:beginningBalanceYearRange:" + [title].join(":"), "5");
  const [endingBalanceYearRange, setEndingBalanceYearRange] = useSharedFilter("UnifiedSummaryReportSection.jsx:UnifiedSummaryReportSection:endingBalanceYearRange:" + [title].join(":"), "5");
  const [receiptsYearRange, setReceiptsYearRange] = useSharedFilter("UnifiedSummaryReportSection.jsx:UnifiedSummaryReportSection:receiptsYearRange:" + [title].join(":"), "5");
  const [expensesYearRange, setExpensesYearRange] = useSharedFilter("UnifiedSummaryReportSection.jsx:UnifiedSummaryReportSection:expensesYearRange:" + [title].join(":"), "5");
  const [contributionStats, setContributionStats] = useState(null);
  const [showTotalSpendingChart, setShowTotalSpendingChart] = useSharedFilter("UnifiedSummaryReportSection.jsx:UnifiedSummaryReportSection:showTotalSpendingChart:" + [title].join(":"), false);
  const [showAverageDonationChart, setShowAverageDonationChart] =
    useSharedFilter("UnifiedSummaryReportSection.jsx:UnifiedSummaryReportSection:showAverageDonationChart:" + [title].join(":"), false);
  const [showTotalDonationsChart, setShowTotalDonationsChart] = useSharedFilter("UnifiedSummaryReportSection.jsx:UnifiedSummaryReportSection:showTotalDonationsChart:" + [title].join(":"), false);
  const [showNumberOfDonationsChart, setShowNumberOfDonationsChart] =
    useSharedFilter("UnifiedSummaryReportSection.jsx:UnifiedSummaryReportSection:showNumberOfDonationsChart:" + [title].join(":"), false);
  const [totalSpendingYearRange, setTotalSpendingYearRange] = useSharedFilter("UnifiedSummaryReportSection.jsx:UnifiedSummaryReportSection:totalSpendingYearRange:" + [title].join(":"), "5");
  const [averageDonationYearRange, setAverageDonationYearRange] = useSharedFilter("UnifiedSummaryReportSection.jsx:UnifiedSummaryReportSection:averageDonationYearRange:" + [title].join(":"), "5");
  const [totalDonationsYearRange, setTotalDonationsYearRange] = useSharedFilter("UnifiedSummaryReportSection.jsx:UnifiedSummaryReportSection:totalDonationsYearRange:" + [title].join(":"), "5");
  const [numberOfDonationsYearRange, setNumberOfDonationsYearRange] =
    useSharedFilter("UnifiedSummaryReportSection.jsx:UnifiedSummaryReportSection:numberOfDonationsYearRange:" + [title].join(":"), "5");
  const [tableYearRange, setTableYearRange] = useSharedFilter("UnifiedSummaryReportSection.jsx:UnifiedSummaryReportSection:tableYearRange:" + [title].join(":"), "5");
  const yearWindowOptions = ["ALL", "1", "3", "5", "10", "15"];

  const suppliedParties = rows
    ? [
        ...new Set(
          rows
            .map((row) => row.party || row["Party Affiliation"])
            .filter(Boolean),
        ),
      ]
    : [];
  const isCombined = rows
    ? suppliedParties.includes("Democrat") &&
      suppliedParties.includes("Republican")
    : docIds.includes("GDTC-Summaries") && docIds.includes("GRTC-Summaries");
  const isDemOnly = rows
    ? suppliedParties.length === 1 && suppliedParties[0] === "Democrat"
    : docIds.length === 1 && docIds[0] === "GDTC-Summaries";
  const isRepOnly = rows
    ? suppliedParties.length === 1 && suppliedParties[0] === "Republican"
    : docIds.length === 1 && docIds[0] === "GRTC-Summaries";

  const normalizedTown = String(townName || "")
    .trim()
    .toLowerCase();
  const normalizedCurrentCommitteeName = String(currentCommitteeName || "")
    .trim()
    .toLowerCase();

  const pdfLookup = useMemo(() => {
    if (collapsed) {
      return new Map();
    }

    const filings = Array.isArray(seecPdfManifest?.committees)
      ? seecPdfManifest.committees.flatMap(
          (committee) => committee.filings || [],
        )
      : [];

    return new Map(
      filings.map((filing) => [
        buildManifestLookupKey(
          filing.committeeName,
          filing.filingEventType,
          filing.periodCovBeginDate,
          filing.periodCovEndDate,
        ),
        filing.pdfUrl,
      ]),
    );
  }, []);

  const getCommitteeColor = (committeeName) => {
    const normalizedCommittee = String(committeeName || "")
      .trim()
      .toLowerCase();

    if (
      normalizedTown &&
      normalizedCommittee === `${normalizedTown} democratic town committee`
    ) {
      return "#2563eb";
    }

    if (
      normalizedTown &&
      normalizedCommittee === `${normalizedTown} republican town committee`
    ) {
      return "#dc2626";
    }

    return getChartColor(committeeName || "committee");
  };

  // Color coding
  let color = "bg-[#e0f2fe]";
  if ((rows && isRepOnly) || (!rows && docIds[0].startsWith("GRTC")))
    color = "bg-[#fee2e2]";

  /**
   * Parse a string like "$250.00" into number 250
   */
  const parseMoney = (val) =>
    typeof val === "number"
      ? val
      : parseFloat(val?.replace(/[^\d.-]/g, "")) || 0;

  const filterPointsByYearWindow = (points, windowValue) => {
    const latestYear = points.length
      ? Math.max(
          ...points
            .map((point) =>
              Number.isFinite(point?.sortDate)
                ? new Date(point.sortDate).getFullYear()
                : null,
            )
            .filter((year) => Number.isFinite(year)),
        )
      : null;

    if (windowValue === "ALL" || latestYear == null) {
      return points;
    }

    const minYear = latestYear - Number(windowValue) + 1;
    return points.filter((point) => {
      if (!Number.isFinite(point?.sortDate)) return false;
      return new Date(point.sortDate).getFullYear() >= minYear;
    });
  };

  /**
   * Fetch summary documents, contributions, and expenditures,
   * and compute stats: yearly averages, totals, and counts.
   */
  useEffect(() => {
    if (collapsed) {
      return undefined;
    }

    async function fetchAndProcess() {
      const allRows = rows
        ? rows.map((row) => ({
            ...row,
            party: row.party || row["Party Affiliation"],
          }))
        : (await loadSeecDocuments(docIds)).flatMap((loadedDoc) => {
            const party = loadedDoc.id.startsWith("GDTC")
              ? "Democrat"
              : "Republican";
            return (loadedDoc.data || []).map((row) => ({ ...row, party }));
          });

      const statsContributionRows = contributionRows;

      const partyContributions = statsContributionRows
        ? Object.entries(
            statsContributionRows.reduce((acc, row) => {
              const party = row.party || row["Party Affiliation"];
              if (!party) return acc;
              if (!acc[party]) acc[party] = [];
              acc[party].push(row);
              return acc;
            }, {}),
          ).map(([party, contributions]) => ({ party, contributions }))
        : await Promise.all(
            [
              docIds.includes("GDTC-Summaries")
                ? { party: "Democrat", docId: "GDTC-Contributions" }
                : null,
              docIds.includes("GRTC-Summaries")
                ? { party: "Republican", docId: "GRTC-Contributions" }
                : null,
            ]
              .filter(Boolean)
              .map(async ({ party, docId }) => {
                const loadedDoc = await loadSeecDocument(docId);
                return {
                  party,
                  contributions: loadedDoc?.data || [],
                };
              }),
          );

      // ---- CALCULATE CHART DATA ----
      const mergedAverages = {}; // year -> { year, Democrat: avg, Republican: avg }
      const mergedTotals = {}; // year -> { year, Democrat: total, Republican: total }
      const mergedCounts = {}; // year -> { year, Democrat: count, Republican: count }

      partyContributions.forEach(({ party, contributions }) => {
        const yearlyTotals = {}; // year -> { total, count }
        contributions.forEach((c) => {
          const amount = parseMoney(c["Contribution Amount"]);
          const rawDate = c.Date;
          const date =
            rawDate instanceof Date
              ? rawDate
              : typeof rawDate?.toDate === "function"
                ? rawDate.toDate()
                : new Date(rawDate);
          const year = date.getFullYear();

          if (!isNaN(year) && !isNaN(amount)) {
            if (!yearlyTotals[year])
              yearlyTotals[year] = { total: 0, count: 0 };
            yearlyTotals[year].total += amount;
            yearlyTotals[year].count += 1;
          }
        });

        Object.entries(yearlyTotals).forEach(([year, stats]) => {
          if (!mergedAverages[year]) mergedAverages[year] = { year };
          if (!mergedTotals[year]) mergedTotals[year] = { year };
          if (!mergedCounts[year]) mergedCounts[year] = { year };
          mergedAverages[year][party] = stats.total / stats.count;
          mergedTotals[year][party] = stats.total;
          mergedCounts[year][party] = stats.count;
        });
      });

      const yearlyAverages = Object.values(mergedAverages).sort(
        (a, b) => a.year - b.year,
      );
      const yearlyTotals = Object.values(mergedTotals).sort(
        (a, b) => a.year - b.year,
      );
      const yearlyCounts = Object.values(mergedCounts).sort(
        (a, b) => a.year - b.year,
      );

      // Add spending aggregation per year per party
      const mergedSpending = {};
      allRows.forEach((row) => {
        const endDate = toJSDate(row["Period Cov. End Date"]);
        if (!endDate) return;
        const year = endDate.getFullYear();
        const amount = parseMoney(row["Expenses Paid by Committee"]);
        if (!mergedSpending[year]) mergedSpending[year] = { year };
        mergedSpending[year][row.party] =
          (mergedSpending[year][row.party] || 0) + amount;
      });

      const yearlySpending = Object.values(mergedSpending).sort(
        (a, b) => a.year - b.year,
      );
      // After defining yearlySpending in useEffect
      setContributionStats({
        yearlyAverages,
        yearlyTotals,
        yearlyCounts,
        yearlySpending,
      });
      // --- Process timeline report data ---
      const mapped = allRows
        .map((row) => {
          const beginDate = toJSDate(row["Period Cov. Begin Date"]);
          const endDate = toJSDate(row["Period Cov. End Date"]);
          const displayDate = endDate
            ? `${(endDate.getMonth() + 1).toString().padStart(2, "0")}-${endDate
                .getDate()
                .toString()
                .padStart(2, "0")}-${endDate.getFullYear()}`
            : "—";

          return {
            Committee: row["Committee"],
            committeeDisplayName: sanitizeCommitteeLabel(
              row["Committee Display Name"] ||
                row["Committee"] ||
                currentCommitteeName,
            ),
            FilingEventType: row["FilingEventType"],
            committeeType: row["Committee Type"] || "—",
            dataSource: row.DS || "—",
            partyAffiliation: row["Party Affiliation"] || "—",
            refiledElectronically: row["R-FE"] || "—",
            status: row.Status || "—",
            beginDate,
            endDate,
            disclosureSubmittedDateSort: toJSDate(
              row["Disclosure Submitted Date"],
            )?.getTime?.() ?? 0,
            periodCovBeginDateSort: toJSDate(
              row["Period Cov. Begin Date"],
            )?.getTime?.() ?? 0,
            periodCovEndDateSort: toJSDate(
              row["Period Cov. End Date"],
            )?.getTime?.() ?? 0,
            disclosureSubmittedDate: row["Disclosure Submitted Date"]
              ? formatMMDDYYYY(toJSDate(row["Disclosure Submitted Date"]))
              : "—",
            periodCovBeginDate: row["Period Cov. Begin Date"]
              ? formatMMDDYYYY(toJSDate(row["Period Cov. Begin Date"]))
              : "—",
            periodCovEndDate: row["Period Cov. End Date"]
              ? formatMMDDYYYY(toJSDate(row["Period Cov. End Date"]))
              : "—",
            beginDateDisplay: formatMMDDYYYY(beginDate),
            endDateDisplay: formatMMDDYYYY(endDate),
            receipts: parseMoney(row["Total Monetary Receipts"]),
            expenses: parseMoney(row["Expenses Paid by Committee"]),
            beginningBalance: parseMoney(
              row["Balance on hand at the beginning of Reproting Period"],
            ),
            endingBalance: parseMoney(
              row["Balance on hand at close of Reporting Period"],
            ),
            beginningLoanBalance: parseMoney(row["Beginning Loan Balance"]),
            displayDate,
            party: row.party,
          };
        })
        .filter((row) => row.beginDate && row.endDate);

      const sorted = mapped.sort((a, b) => a.endDate - b.endDate);
      const currentYear = new Date().getFullYear();
      const filtered =
        tableYearRange === "ALL"
          ? sorted
          : sorted.filter(
              (row) =>
                row.endDate.getFullYear() >=
                currentYear - parseInt(tableYearRange, 10) + 1,
            );
      setReportData(
        filtered.map((row) => ({
          ...row,
          pdfUrl:
            pdfLookup.get(
              buildManifestLookupKey(
                row.committeeDisplayName,
                row.FilingEventType,
                row.beginDateDisplay,
                row.endDateDisplay,
              ),
            ) || null,
        })),
      );
    }

    fetchAndProcess();

    return undefined;
  }, [
    collapsed,
    docIds,
    rows,
    contributionRows,
    tableYearRange,
    pdfLookup,
    currentCommitteeName,
  ]);

  /**
   * Converts report rows into chart-ready format keyed by filing date
   */
  const getCommitteeChartData = (key) => {
    const sourceRows =
      Array.isArray(comparisonRows) && comparisonRows.length
        ? comparisonRows
        : rows;
    const grouped = {};

    for (const row of sourceRows || []) {
      const endDate = toJSDate(row["Period Cov. End Date"]);
      if (!endDate) continue;

      const displayDate = formatMMDDYYYY(endDate);
      const committeeLabel = sanitizeCommitteeLabel(
        row["Committee Display Name"] ||
          currentCommitteeName ||
          row.Committee ||
          "Unknown Committee",
      );

      if (!grouped[displayDate]) {
        grouped[displayDate] = {
          name: displayDate,
          sortDate: endDate.getTime(),
        };
      }

      const valueByKey = {
        receipts: parseMoney(row["Total Monetary Receipts"]),
        expenses: parseMoney(row["Expenses Paid by Committee"]),
        beginningBalance: parseMoney(
          row["Balance on hand at the beginning of Reproting Period"],
        ),
        endingBalance: parseMoney(
          row["Balance on hand at close of Reporting Period"],
        ),
      };

      grouped[displayDate][committeeLabel] = valueByKey[key] ?? 0;
    }

    return Object.values(grouped).sort((a, b) => a.sortDate - b.sortDate);
  };

  const getCommitteeSeries = (points) => {
    const sourceRows =
      Array.isArray(comparisonRows) && comparisonRows.length
        ? comparisonRows
        : rows;

    const committeeNames = Array.from(
      new Set(
        (sourceRows || [])
          .map((row) => {
            const label = sanitizeCommitteeLabel(
              row["Committee Display Name"] ||
                currentCommitteeName ||
                row.Committee ||
                null,
            );
            return label || null;
          })
          .filter(Boolean),
      ),
    ).sort((a, b) => {
      const aNorm = String(a).trim().toLowerCase();
      const bNorm = String(b).trim().toLowerCase();
      const demKey = `${normalizedTown} democratic town committee`;
      const repKey = `${normalizedTown} republican town committee`;

      if (aNorm === demKey && bNorm !== demKey) return -1;
      if (bNorm === demKey && aNorm !== demKey) return 1;
      if (aNorm === repKey && bNorm !== repKey) return -1;
      if (bNorm === repKey && aNorm !== repKey) return 1;
      return a.localeCompare(b);
    });

    return committeeNames.map((committeeName) => ({
      key: committeeName,
      label: committeeName,
      data: points.map((point) =>
        Number.isFinite(point[committeeName]) ? point[committeeName] : null,
      ),
      group: "committees",
      color: getCommitteeColor(committeeName),
      valueType: "currency",
      defaultVisible:
        String(committeeName || "")
          .trim()
          .toLowerCase() === normalizedCurrentCommitteeName,
    }));
  };

  const buildPartySeries = (points) => {
    const series = [];

    if (!isRepOnly) {
      series.push({
        key: "Democrat",
        label: "Democrat",
        data: points.map((point) =>
          Number.isFinite(point.Democrat) ? point.Democrat : null,
        ),
        color: "#2563eb",
        valueType: "currency",
      });
    }

    if (!isDemOnly) {
      series.push({
        key: "Republican",
        label: "Republican",
        data: points.map((point) =>
          Number.isFinite(point.Republican) ? point.Republican : null,
        ),
        color: "#dc2626",
        valueType: "currency",
      });
    }

    return series;
  };

  const buildCountSeries = (points) => {
    const series = [];

    if (!isRepOnly) {
      series.push({
        key: "Democrat",
        label: "Democrat",
        data: points.map((point) =>
          Number.isFinite(point.Democrat) ? point.Democrat : null,
        ),
        color: "#2563eb",
        valueType: "count",
      });
    }

    if (!isDemOnly) {
      series.push({
        key: "Republican",
        label: "Republican",
        data: points.map((point) =>
          Number.isFinite(point.Republican) ? point.Republican : null,
        ),
        color: "#dc2626",
        valueType: "count",
      });
    }

    return series;
  };

  /**
   * Render line chart for metrics like receipts or balances
   */
  const renderLineChart = (title, key, windowValue) => {
    const points = filterPointsByYearWindow(
      getCommitteeChartData(key),
      windowValue,
    );
    return (
      <div className="w-full">
        <LineChartWithToggles
          labels={points.map((point) => point.name)}
          series={getCommitteeSeries(points)}
          title={null}
          showTotalWhenNoneSelected={false}
          showSeriesToggles={true}
          showChartTypeSelector={true}
          initialChartType="area"
          spanGaps={true}
          defaultYAxisMode="zero"
          yAxisTitle={title}
          showLegend={false}
        />
      </div>
    );
  };

  const filterYearSeries = (rows, windowValue) => {
    const safeRows = Array.isArray(rows) ? rows : [];
    if (windowValue === "ALL") return safeRows;
    if (!safeRows.length) return safeRows;

    const latestYear = Math.max(
      ...safeRows
        .map((row) => Number(row?.year))
        .filter((year) => Number.isFinite(year)),
    );

    if (!Number.isFinite(latestYear)) return safeRows;

    const minYear = latestYear - Number(windowValue) + 1;
    return safeRows.filter((row) => Number(row?.year) >= minYear);
  };

  const renderBarChart = (
    title,
    rows,
    buildSeries,
    yAxisTitle,
    windowValue,
  ) => {
    const filteredRows = filterYearSeries(rows, windowValue);
    return (
      <div className="w-full">
        <LineChartWithToggles
          labels={filteredRows.map((row) => String(row.year))}
          series={buildSeries(filteredRows)}
          title={null}
          showTotalWhenNoneSelected={false}
          showSeriesToggles={false}
          showChartTypeSelector={true}
          initialChartType="bar"
          defaultYAxisMode="zero"
          yAxisTitle={yAxisTitle}
          showLegend={true}
        />
      </div>
    );
  };

  // Render full UI
  return (
    <div className="mb-4 border border-gray-300 rounded">
      <button type="button" aria-expanded={!collapsed}
        className="w-full bg-gray-100 px-3 py-3 font-semibold text-lg text-gray-900 flex justify-between items-center text-left hover:bg-gray-200 rounded-none"
        onClick={toggle}>
        {title}
        <span aria-hidden="true">{collapsed ? "▸" : "▾"}</span>
      </button>

      {!collapsed && (
        <div className="bg-white p-4">
          <div className="flex flex-col gap-4 mb-6">
            <div className="text-md font-semibold flex justify-between items-center gap-4 flex-wrap">
              <button type="button" aria-expanded={showBeginningBalanceChart}
                className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
                onClick={() => setShowBeginningBalanceChart((prev) => !prev)}
                >
                <span>Balance at Start of Period</span>
                <span aria-hidden="true">{showBeginningBalanceChart ? "▼" : "▶"}</span>
              </button>
              <div className="flex items-center gap-2 text-xs">
                <span className="font-semibold text-gray-700">
                  Number of Years
                </span>
                <select
                  className="border rounded px-2 py-1 text-xs bg-white"
                  value={beginningBalanceYearRange}
                  onChange={(e) => setBeginningBalanceYearRange(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                >
                  {yearWindowOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {showBeginningBalanceChart &&
              renderLineChart(
                "Balance at Start of Period",
                "beginningBalance",
                beginningBalanceYearRange,
              )}

            <div className="text-md font-semibold flex justify-between items-center gap-4 flex-wrap">
              <button type="button" aria-expanded={showEndingBalanceChart}
                className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
                onClick={() => setShowEndingBalanceChart((prev) => !prev)}
                >
                <span>Balance at End of Period</span>
                <span aria-hidden="true">{showEndingBalanceChart ? "▼" : "▶"}</span>
              </button>
              <div className="flex items-center gap-2 text-xs">
                <span className="font-semibold text-gray-700">
                  Number of Years
                </span>
                <select
                  className="border rounded px-2 py-1 text-xs bg-white"
                  value={endingBalanceYearRange}
                  onChange={(e) => setEndingBalanceYearRange(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                >
                  {yearWindowOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {showEndingBalanceChart &&
              renderLineChart(
                "Balance at End of Period",
                "endingBalance",
                endingBalanceYearRange,
              )}

            <div className="text-md font-semibold flex justify-between items-center gap-4 flex-wrap">
              <button type="button" aria-expanded={showReceiptsChart}
                className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
                onClick={() => setShowReceiptsChart((prev) => !prev)}
                >
                <span>Total Monetary Receipts</span>
                <span aria-hidden="true">{showReceiptsChart ? "▼" : "▶"}</span>
              </button>
              <div className="flex items-center gap-2 text-xs">
                <span className="font-semibold text-gray-700">
                  Number of Years
                </span>
                <select
                  className="border rounded px-2 py-1 text-xs bg-white"
                  value={receiptsYearRange}
                  onChange={(e) => setReceiptsYearRange(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                >
                  {yearWindowOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {showReceiptsChart &&
              renderLineChart(
                "Total Monetary Receipts",
                "receipts",
                receiptsYearRange,
              )}

            <div className="text-md font-semibold flex justify-between items-center gap-4 flex-wrap">
              <button type="button" aria-expanded={showExpensesChart}
                className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
                onClick={() => setShowExpensesChart((prev) => !prev)}
                >
                <span>Expenses Paid by Committee</span>
                <span aria-hidden="true">{showExpensesChart ? "▼" : "▶"}</span>
              </button>
              <div className="flex items-center gap-2 text-xs">
                <span className="font-semibold text-gray-700">
                  Number of Years
                </span>
                <select
                  className="border rounded px-2 py-1 text-xs bg-white"
                  value={expensesYearRange}
                  onChange={(e) => setExpensesYearRange(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                >
                  {yearWindowOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {showExpensesChart &&
              renderLineChart(
                "Expenses Paid by Committee",
                "expenses",
                expensesYearRange,
              )}
          </div>

          <div className="my-6 border-t border-gray-300" />

          {contributionStats && (
            <div className="flex flex-col gap-4 mb-6">
              <div className="text-md font-semibold flex justify-between items-center gap-4 flex-wrap">
                <button type="button" aria-expanded={showTotalSpendingChart}
                  className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
                  onClick={() => setShowTotalSpendingChart((prev) => !prev)}
                  >
                  <span>Total Spending by Year</span>
                  <span aria-hidden="true">{showTotalSpendingChart ? "▼" : "▶"}</span>
                </button>
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold text-gray-700">
                    Number of Years
                  </span>
                  <select
                    className="border rounded px-2 py-1 text-xs bg-white"
                    value={totalSpendingYearRange}
                    onChange={(e) => setTotalSpendingYearRange(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {yearWindowOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {showTotalSpendingChart &&
                renderBarChart(
                  "Total Spending by Year",
                  contributionStats.yearlySpending,
                  buildPartySeries,
                  "Total Spending",
                  totalSpendingYearRange,
                )}

              <div className="text-md font-semibold flex justify-between items-center gap-4 flex-wrap">
                <button type="button" aria-expanded={showAverageDonationChart}
                  className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
                  onClick={() => setShowAverageDonationChart((prev) => !prev)}
                  >
                  <span>Average Donation by Year</span>
                  <span aria-hidden="true">{showAverageDonationChart ? "▼" : "▶"}</span>
                </button>
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold text-gray-700">
                    Number of Years
                  </span>
                  <select
                    className="border rounded px-2 py-1 text-xs bg-white"
                    value={averageDonationYearRange}
                    onChange={(e) =>
                      setAverageDonationYearRange(e.target.value)
                    }
                    onClick={(e) => e.stopPropagation()}
                  >
                    {yearWindowOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {showAverageDonationChart &&
                renderBarChart(
                  "Average Donation by Year",
                  contributionStats.yearlyAverages,
                  buildPartySeries,
                  "Average Donation",
                  averageDonationYearRange,
                )}

              <div className="text-md font-semibold flex justify-between items-center gap-4 flex-wrap">
                <button type="button" aria-expanded={showTotalDonationsChart}
                  className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
                  onClick={() => setShowTotalDonationsChart((prev) => !prev)}
                  >
                  <span>Total Donations by Year</span>
                  <span aria-hidden="true">{showTotalDonationsChart ? "▼" : "▶"}</span>
                </button>
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold text-gray-700">
                    Number of Years
                  </span>
                  <select
                    className="border rounded px-2 py-1 text-xs bg-white"
                    value={totalDonationsYearRange}
                    onChange={(e) => setTotalDonationsYearRange(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {yearWindowOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {showTotalDonationsChart &&
                renderBarChart(
                  "Total Donations by Year",
                  contributionStats.yearlyTotals,
                  buildPartySeries,
                  "Total Donations",
                  totalDonationsYearRange,
                )}

              <div className="text-md font-semibold flex justify-between items-center gap-4 flex-wrap">
                <button type="button" aria-expanded={showNumberOfDonationsChart}
                  className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
                  onClick={() => setShowNumberOfDonationsChart((prev) => !prev)}
                  >
                  <span>Number of Donations by Year</span>
                  <span aria-hidden="true">{showNumberOfDonationsChart ? "▼" : "▶"}</span>
                </button>
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold text-gray-700">
                    Number of Years
                  </span>
                  <select
                    className="border rounded px-2 py-1 text-xs bg-white"
                    value={numberOfDonationsYearRange}
                    onChange={(e) =>
                      setNumberOfDonationsYearRange(e.target.value)
                    }
                    onClick={(e) => e.stopPropagation()}
                  >
                    {yearWindowOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {showNumberOfDonationsChart &&
                renderBarChart(
                  "Number of Donations by Year",
                  contributionStats.yearlyCounts,
                  buildCountSeries,
                  "Number of Donations",
                  numberOfDonationsYearRange,
                )}
            </div>
          )}

          <div className="my-6 border-t border-gray-300" />

          {!isCombined && (
            <div className="overflow-auto">
              <div className="text-md font-semibold mb-2 flex justify-between items-center gap-4 flex-wrap">
                <button type="button" aria-expanded={showTable}
                  className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
                  onClick={() => setShowTable((prev) => !prev)}
                  >
                  <span>Report Table</span>
                  <span aria-hidden="true">{showTable ? "▼" : "▶"}</span>
                </button>
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold text-gray-700">
                    Number of Years
                  </span>
                  <select
                    className="border rounded px-2 py-1 text-xs bg-white"
                    value={tableYearRange}
                    onChange={(e) => setTableYearRange(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {yearWindowOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {showTable && (
                <CommonTable
                  title=""
                  columns={[
                    {
                      key: "FilingEventType",
                      label: "Filing Event Type",
                      style: { width: "180px" },
                      render: (row) => {
                        const detailRows = buildSummaryPopoverRows(row);
                        const content = (
                          <div className="p-3 text-xs text-gray-800">
                            <div className="font-semibold text-sm text-gray-900 mb-2">
                              {row.FilingEventType}
                            </div>
                            <table className="w-full text-xs">
                              <tbody>
                                {detailRows.map((item) => (
                                  <tr
                                    key={`${row.FilingEventType}-${item.key}`}
                                    className="border-b border-gray-100 last:border-b-0"
                                  >
                                    <td className="px-2 py-1 align-top font-semibold text-gray-600 whitespace-nowrap">
                                      {item.label}
                                    </td>
                                    <td className="px-2 py-1 align-top text-gray-900 break-words">
                                      {item.value}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );

                        return (
                          <div className="relative flex items-center min-h-[22px] pr-6">
                            {row.pdfUrl ? (
                              <a
                                href={row.pdfUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-700 hover:text-blue-900 underline"
                              >
                                {row.FilingEventType}
                              </a>
                            ) : (
                              <span>{row.FilingEventType}</span>
                            )}
                            <div className="absolute right-1 top-1/2 -translate-y-1/2">
                              <HoverPopover
                                content={content}
                                popoverClassName="text-gray-800"
                                maxWidth={760}
                              >
                                <InformationCircleIcon
                                  className="w-4 h-4 text-gray-500 hover:text-gray-800 cursor-pointer"
                                  aria-label={`Show filing details for ${row.FilingEventType}`}
                                />
                              </HoverPopover>
                            </div>
                          </div>
                        );
                      },
                    },
                    {
                      key: "beginningBalance",
                      label:
                        "Balance on hand at the beginning of Reporting Period",
                      style: { width: "130px" },       align: "right",
                      render: (row) =>
                        `$${(row.beginningBalance ?? 0).toLocaleString()}`,
                    },
                    {
                      key: "receipts",
                      label: "Total Monetary Receipts",
                      style: { width: "100px" }, align: "right",
                      render: (row) =>
                        `$${(row.receipts ?? 0).toLocaleString()}`,
                    },
                    {
                      key: "expenses",
                      label: "Expenses Paid by Committee",
                      style: { width: "100px" }, align: "right",
                      render: (row) =>
                        `$${(row.expenses ?? 0).toLocaleString()}`,
                    },
                    {
                      key: "endingBalance",
                      label: "Balance on hand at close of Reporting Period",
                      style: { width: "130px" }, align: "right",
                      render: (row) =>
                        `$${(row.endingBalance ?? 0).toLocaleString()}`,
                    },
                    {
                      key: "beginningLoanBalance",
                      label: "Beginning Loan Balance",
                      style: { width: "90px" }, align: "right",
                      render: (row) =>
                        `$${(row.beginningLoanBalance ?? 0).toLocaleString()}`,
                    },
                    {
                      key: "disclosureSubmittedDate",
                      label: "Disclosure Submitted Date",
                      style: { width: "100px" }, align: "center",
                      sortKey: "disclosureSubmittedDateSort",
                    },
                    {
                      key: "periodCovBeginDate",
                      label: "Period Cov. Begin Date",
                      style: { width: "100px" }, align: "center",
                      sortKey: "periodCovBeginDateSort",
                    },
                    {
                      key: "periodCovEndDate",
                      label: "Period Cov. End Date",
                      style: { width: "100px" }, align: "center",
                      sortKey: "periodCovEndDateSort",
                    },
                  ]}
                  data={reportData}
                  headerColor={color}
                  showAggregateTotal={false}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default UnifiedSummaryReportSection;
